import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { confirmEmailChange } from "@/services/customerService";
import { useAuth } from "@/components/AuthContext";
import { Mail, CheckCircle, XCircle, Loader2 } from 'lucide-react';

const ConfirmEmailChangePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('無効なトークンです');
      return;
    }

    const confirmChange = async () => {
      try {
        const response = await confirmEmailChange(token);
        setStatus('success');
        setMessage(response.message);

        // ログイン状態の場合のみユーザー情報を更新
        try {
          await refreshUser();
          // 3秒後にプロフィールページへリダイレクト
          setTimeout(() => {
            navigate('/profile');
          }, 3000);
        } catch {
          // ログインしていない場合はログインページへリダイレクト
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      } catch (error: any) {
        const errorData = error.response?.data;

        // 既に使用済み・完了済みの場合は、エラーではなく情報として表示
        if (errorData?.already_used || errorData?.already_completed) {
          setStatus('success');
          setMessage(errorData.message || 'メールアドレスの変更は既に完了しています。');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setStatus('error');
          setMessage(errorData?.error || errorData?.message || 'エラーが発生しました');
        }
      }
    };

    confirmChange();
  }, [token, refreshUser, navigate]);

  return (
    <>
      <Helmet>
        <title>メールアドレス変更確認</title>
        <meta property="og:title" content="メールアドレス変更確認" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg">
        <div className="pt-20">
          <div className="max-w-md mx-auto px-4 py-10">
            <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-400 border-theme rounded-theme-lg flex items-center justify-center shadow-theme-sm">
                  <Mail className="w-6 h-6 text-black" />
                </div>
              </div>
              <h1 className="text-2xl [font-weight:var(--theme-font-weight)] text-center text-black mb-6">メールアドレス変更確認</h1>

              {status === 'loading' && (
                <div className="text-center py-8">
                  <Loader2 className="w-12 h-12 animate-spin mx-auto text-theme-primary mb-4" />
                  <p className="[font-weight:var(--theme-font-weight-button)] text-gray-600">処理中...</p>
                </div>
              )}

              {status === 'success' && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                    <CheckCircle className="w-8 h-8 text-black" />
                  </div>
                  <p className="[font-weight:var(--theme-font-weight-button)] text-green-700 mb-4">{message}</p>
                  <p className="text-sm [font-weight:var(--theme-font-weight-button)] text-gray-600 mb-6">メールアドレスの変更が完了しました</p>
                  <a
                    href="/login"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    ログインページへ
                  </a>
                </div>
              )}

              {status === 'error' && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400 border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme-sm">
                    <XCircle className="w-8 h-8 text-black" />
                  </div>
                  <p className="[font-weight:var(--theme-font-weight-button)] text-red-700 mb-6">{message}</p>
                  <a
                    href="/login"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-black shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    ログインページへ
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConfirmEmailChangePage;
